import { useEffect, useRef, useState, useCallback } from 'react';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Layers, Search, AlertCircle, RefreshCw, ShieldCheck, ChevronDown, Info, X } from 'lucide-react';
import { fetchMapArea, oppTypeParam, OPENFREEMAP_STYLE, US_DEFAULT } from '@/lib/mapApi';
import { skillbridgeDisplay } from '@/lib/skillbridge';

// MapLibre resolves its tile-parsing web worker from `import.meta.url`, which our webpack
// bundle rewrites to a non-http value — MapLibre then falls back to `new Worker('')`, a dead
// worker that can spawn but never fetches/parses VECTOR tiles (raster still works on the main
// thread), leaving the basemap blank. Point it at MapLibre's own worker bundle, served
// same-origin from /public (its sibling ./maplibre-gl-shared.mjs is served alongside it).
if (typeof maplibregl.setWorkerUrl === 'function') {
  maplibregl.setWorkerUrl(`${process.env.PUBLIC_URL || ''}/maplibre-gl-worker.mjs`);
}

const JOB_COLOR = '#0f7d3d';       // Sergeant TAP brand green
const SB_COLOR = '#d97706';        // SkillBridge amber identity
const CONT_COLOR = '#3b5bdb';      // complementary indigo for Verified Guard & Reserve
const CONT_COMPONENTS = ['Army Reserve', 'Army National Guard', 'Air National Guard',
  'Air Force Reserve', 'Navy Reserve', 'Marine Corps Reserve', 'Coast Guard Reserve'];

function boundsFromMap(map) {
  const b = map.getBounds();
  return { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
}

// Client-side mirror of the backend area safety limit (services/map_service.validate_bounds:
// lat span > 85 or lng span > 180 → "viewport too large"). Used to guide the user with a clear
// "Zoom in to search this area." BEFORE issuing a request the backend would reject — never to
// loosen or bypass the backend limit.
function isOversized(b) {
  if (!b) return false;
  return (b.north - b.south) > 85 || (b.east - b.west) > 180;
}

// GeoJSON builders from the bounded API payload (backend already clusters/caps).
function jobFeatures(data, zoom) {
  if (!data?.jobs) return { pins: [], clusters: [] };
  if (data.jobs.type === 'individual') {
    // Individual mode returns ONE feature per job, but many jobs geocode to the SAME (or a
    // sub-pixel-close) coordinate — e.g. 34 Hilton Head jobs at 32.2163,-80.7526, or a "17" and a
    // "2" group ~5m apart. Rendered raw they stack on one pixel: the user reads one marker's number
    // but the click hit-tests a DIFFERENT overlapping marker → "marker 17 -> popup 2". Fix: snap
    // jobs into a screen-space cell (~one marker footprint at the current zoom) so any two DISTINCT
    // markers are visually separated and unambiguous, and every job at one place shares ONE marker.
    // A cell with 1 job stays an ordinary pin at its REAL coordinate (no snapping/jitter); a cell
    // with 2+ becomes a count-bearing marker positioned at the members' real centroid. The count,
    // the clicked group and the popup list are ALL the same member array → they can never diverge.
    const valid = (data.jobs.pins || []).filter(p => p.lat != null && p.lng != null);
    const z = (zoom == null ? 10 : zoom);
    // MapLibre worldSize = 512 * 2^zoom px; a ~48px cell ≈ the count-marker footprint in degrees.
    const cell = Math.max(48 * (360 / (512 * Math.pow(2, z))), 1e-6);
    const groups = new Map();
    for (const p of valid) {
      const key = `${Math.floor(p.lng / cell)},${Math.floor(p.lat / cell)}`;
      let g = groups.get(key);
      if (!g) { g = []; groups.set(key, g); }
      g.push(p);
    }
    const pins = [];
    const clusters = [];
    groups.forEach((group) => {
      if (group.length === 1) { pins.push(jobPinFeature(group[0])); return; }
      const lng = group.reduce((sum, p) => sum + p.lng, 0) / group.length;
      const lat = group.reduce((sum, p) => sum + p.lat, 0) / group.length;
      clusters.push({
        type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { kind: 'job_cluster', count: group.length, colocated: 1,
          loc: group[0].location || '',
          // FULL member list (no cap) so popup length ALWAYS equals the displayed count.
          jobs: JSON.stringify(group.map(p => ({
            job_id: p.job_id, title: p.title || '', employer: p.employer || '',
            sb: (p.opportunity_type === 'skillbridge') ? 1 : 0,
            match_score: p.match_score == null ? '' : String(p.match_score) }))) } });
    });
    return { pins, clusters };
  }
  return { pins: [], clusters: (data.jobs.clusters || []).map(c => ({
    type: 'Feature', geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
    properties: { kind: 'job_cluster', count: c.count,
      w: c.bounds?.west, s: c.bounds?.south, e: c.bounds?.east, n: c.bounds?.north } })) };
}

function contFeatures(data, zoom) {
  if (!data?.continuity) return { pins: [], clusters: [] };
  if (data.continuity.type === 'individual') {
    // Continuity individual mode returns facility-grouped records, but many facilities/records
    // sit at the SAME coordinate (e.g. Fort Liberty: 4 facilities = 16 records all at one point).
    // Rendered as plain stacked circles they overlap on one pixel and the "16" a user saw in
    // cluster mode has no legible representation after drill-down (looks like it disappeared).
    // Mirror the job fix: snap co-located records into a zoom-aware cell — a cell with a single
    // record stays a plain pin at its REAL coordinate; a cell with multiple records/units becomes a
    // count-bearing BLUE marker whose count is the RAW RECORD count (sum of unit_count) so the
    // number keeps the SAME meaning as the parent cluster (verified-presence records, not vacancies).
    const valid = (data.continuity.pins || []).filter(p => p.lat != null && p.lng != null);
    const z = (zoom == null ? 10 : zoom);
    const cell = Math.max(48 * (360 / (512 * Math.pow(2, z))), 1e-6);
    const groups = new Map();
    for (const p of valid) {
      const key = `${Math.floor(p.lng / cell)},${Math.floor(p.lat / cell)}`;
      let g = groups.get(key);
      if (!g) { g = []; groups.set(key, g); }
      g.push(p);
    }
    const pins = [];
    const clusters = [];
    groups.forEach((group) => {
      const totalUnits = group.reduce((sum, p) => sum + (p.unit_count || 1), 0);
      if (group.length === 1 && (group[0].unit_count || 1) === 1) {
        pins.push(contPinFeature(group[0]));
        return;
      }
      const lng = group.reduce((sum, p) => sum + p.lng, 0) / group.length;
      const lat = group.reduce((sum, p) => sum + p.lat, 0) / group.length;
      const g0 = group[0];
      clusters.push({
        type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { kind: 'continuity_cluster', count: totalUnits, colocated: 1,
          loc: [g0.city, g0.state].filter(Boolean).join(', '),
          facilities: JSON.stringify(group.map(p => ({
            site_name: p.site_name || '', city: p.city || '', state: p.state || '',
            components: (p.components || []).join(', '), unit_count: p.unit_count || 1,
            official_url: p.official_url || '' }))) } });
    });
    return { pins, clusters };
  }
  return { pins: [], clusters: (data.continuity.clusters || []).map(c => ({
    type: 'Feature', geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
    properties: { kind: 'continuity_cluster', count: c.count,
      w: c.bounds?.west, s: c.bounds?.south, e: c.bounds?.east, n: c.bounds?.north } })) };
}

const fc = (features) => ({ type: 'FeatureCollection', features });

// Escape dynamic strings before HTML injection in map popups (defense-in-depth even though
// job/continuity fields are provider/government sourced).
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g,
  c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// A single ordinary job pin feature.
function jobPinFeature(p) {
  return {
    type: 'Feature', geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
    properties: { kind: 'job', sb: (p.opportunity_type === 'skillbridge') ? 1 : 0,
      job_id: p.job_id, title: p.title || '', employer: p.employer || '',
      opportunity_type: p.opportunity_type || 'employment',
      match_score: p.match_score == null ? '' : String(p.match_score) },
  };
}

// A single ordinary continuity (Verified Guard & Reserve) location pin feature.
function contPinFeature(p) {
  return {
    type: 'Feature', geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
    properties: { kind: 'continuity', site_name: p.site_name || '', city: p.city || '',
      state: p.state || '', unit_count: p.unit_count || 1,
      components: (p.components || []).join(', '), precision: p.location_precision || '',
      official_url: p.official_url || '', source_org: p.source_org || '' },
  };
}

export default function OpportunitiesMap({ visible, jobsSelected, sbSelected, opportunityType,
  remote, jobType, savedOnly, savedNonce, initialCenter, selectedJobId, onSelectJob, onOpenJob, focus }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(false);
  // True when the current viewport is larger than the searchable area limit — we then guide the
  // user to zoom in instead of firing an oversized (and backend-rejected) request.
  const [tooLarge, setTooLarge] = useState(false);
  const [showJobs, setShowJobs] = useState(true);
  const [showCont, setShowCont] = useState(true);
  const [contComponent, setContComponent] = useState('');
  // Collapsible Map Layers overlay (recover map space). MOBILE (<768px) collapsed by default,
  // DESKTOP expanded; a user's choice is remembered for the browser session (sessionStorage).
  const [layersCollapsed, setLayersCollapsed] = useState(() => {
    const saved = typeof window !== 'undefined' ? sessionStorage.getItem('map_layers_collapsed') : null;
    if (saved !== null) return saved === '1';
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const [infoOpen, setInfoOpen] = useState(false);
  const toggleLayers = () => setLayersCollapsed(c => {
    const n = !c;
    try { sessionStorage.setItem('map_layers_collapsed', n ? '1' : '0'); } catch { /* ignore */ }
    if (n) setInfoOpen(false);
    return n;
  });
  const stateRef = useRef({});
  // When true, the NEXT map 'moveend' is an EXPLICIT drill-down/recenter (cluster click or a
  // location/radius change) and should auto-fire exactly one bounded search — NOT show
  // "Search this area". Ordinary drag/wheel/pinch leave it false → they only set `dirty`.
  const autoSearchRef = useRef(false);
  // When true, the NEXT map 'moveend' is a purely VISUAL card→pin focus — it must NOT run a search
  // or surface "Search this area" (0 automatic /api/map/opportunities requests).
  const silentMoveRef = useRef(false);

  // keep latest filter/layer state for the (event-driven) search handler
  stateRef.current = { showJobs, showCont, contComponent, jobsSelected, sbSelected,
    opportunityType, remote, jobType, savedOnly };

  const runSearch = useCallback(async () => {
    const map = mapRef.current; if (!map) return;
    const bounds = boundsFromMap(map);
    // Oversized viewport → do NOT issue an automatic/manual oversized query the backend would
    // reject. Surface a clear "Zoom in to search this area." instead of a scary generic failure.
    if (isOversized(bounds)) {
      setTooLarge(true); setDirty(false); setError(false); setLoading(false); setInitialLoad(false);
      return;
    }
    setTooLarge(false);
    const s = stateRef.current;
    // Always fetch BOTH layers so each source stays populated even while a layer is toggled OFF —
    // this lets the checkboxes show/hide instantly with ZERO API calls. Which layers are VISIBLE is
    // controlled purely by MapLibre layer visibility (see the showJobs/showCont effects).
    const layers = ['jobs', 'continuity'];
    setError(false); setLoading(true); setDirty(false);
    try {
      let ot = s.opportunityType;
      if (!ot) ot = oppTypeParam({ jobs: s.jobsSelected, skillbridge: s.sbSelected });
      const data = await fetchMapArea({
        bounds, zoom: map.getZoom(), layers,
        opportunity_type: ot, remote: s.remote, job_type: s.jobType,
        saved_only: s.savedOnly || undefined,
        component: s.contComponent || undefined,
        vw: containerRef.current?.clientWidth || undefined,
      });
      const jf = jobFeatures(data, map.getZoom());
      const cf = contFeatures(data, map.getZoom());
      map.getSource('jobs-pins')?.setData(fc(jf.pins));
      map.getSource('jobs-clusters')?.setData(fc(jf.clusters));
      map.getSource('cont-pins')?.setData(fc(cf.pins));
      map.getSource('cont-clusters')?.setData(fc(cf.clusters));
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false); setInitialLoad(false);
    }
  }, []);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    // Restore the last map viewport for this session (e.g. returning from the full job page)
    // so center/zoom are preserved. Falls back to the seeker's Home/Base, then a US view.
    let saved = null;
    try {
      const raw = sessionStorage.getItem('opps_map_view_v1');
      if (raw) { const v = JSON.parse(raw); if (v && Date.now() - (v.ts || 0) <= 120000) saved = v; }
    } catch { /* ignore */ }
    // PRIORITY: when a location anchor with valid coordinates is active (Home / Current Base /
    // Another Location), center/zoom on THAT search origin at a radius-appropriate zoom — even on
    // a fresh mount (List → Map/Split). This overrides any stale persisted (possibly world-scale)
    // viewport so entering the map never opens at an irrelevant world view. Uses the existing
    // focus/recenter values; the focus effect skips this first key so there is no double movement.
    const hasFocus = focus && focus.lng != null && focus.lat != null;
    let center, zoom;
    if (hasFocus) {
      center = [focus.lng, focus.lat];
      zoom = focus.zoom ?? 9;
    } else if (saved) {
      center = [saved.lng, saved.lat];
      zoom = saved.zoom;
    } else if (initialCenter && initialCenter.lng != null) {
      center = [initialCenter.lng, initialCenter.lat];
      zoom = 9;
    } else {
      center = US_DEFAULT.center;
      zoom = US_DEFAULT.zoom;
    }
    // Record that init already centered on this focus so the focus effect does NOT double-move.
    // When init did NOT consume a focus (coords not yet loaded → US default), leave the key null so
    // the FIRST focus that arrives WITH coordinates recenters (the default Split/Map entry path,
    // where the map mounts before the resume coordinates resolve).
    if (hasFocus) lastFocusKey.current = focus.key;
    const map = new maplibregl.Map({
      container: containerRef.current, style: OPENFREEMAP_STYLE, center, zoom,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.AttributionControl({
      compact: true,
      customAttribution: '\u00A9 OpenFreeMap \u00A9 OpenStreetMap contributors',
    }), 'bottom-right');
    mapRef.current = map;

    // Surface init/style/tile failures instead of leaving a silent blank panel.
    map.on('error', (e) => {
      if (e?.error) console.warn('OpportunitiesMap map error:', e.error?.message || e.error);
    });

    map.on('load', () => {
      // Attribution stays compact (native ⓘ) — collapse the initial expanded state so it does
      // not consume map space. MapLibre's own toggle still expands/collapses it on click; the
      // required attribution text is never removed. Only the initial `-show` class is cleared.
      requestAnimationFrame(() => {
        map.getContainer()
          .querySelectorAll('.maplibregl-ctrl-attrib.maplibregl-compact-show')
          .forEach((el) => el.classList.remove('maplibregl-compact-show'));
      });
      const empty = fc([]);
      ['jobs-clusters', 'jobs-pins', 'cont-clusters', 'cont-pins'].forEach(id =>
        map.addSource(id, { type: 'geojson', data: empty }));
      // continuity clusters
      map.addLayer({ id: 'cont-clusters-c', source: 'cont-clusters', type: 'circle',
        paint: { 'circle-color': CONT_COLOR, 'circle-opacity': 0.85,
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 14, 500, 30],
          'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
      map.addLayer({ id: 'cont-clusters-t', source: 'cont-clusters', type: 'symbol',
        layout: { 'text-field': ['get', 'count'], 'text-size': 11, 'text-font': ['Noto Sans Bold'] },
        paint: { 'text-color': '#fff' } });
      // job clusters
      map.addLayer({ id: 'jobs-clusters-c', source: 'jobs-clusters', type: 'circle',
        paint: { 'circle-color': JOB_COLOR, 'circle-opacity': 0.88,
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 15, 5000, 34],
          'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
      map.addLayer({ id: 'jobs-clusters-t', source: 'jobs-clusters', type: 'symbol',
        layout: { 'text-field': ['get', 'count'], 'text-size': 11, 'text-font': ['Noto Sans Bold'] },
        paint: { 'text-color': '#fff' } });
      // continuity pins
      map.addLayer({ id: 'cont-pins-c', source: 'cont-pins', type: 'circle',
        paint: { 'circle-color': CONT_COLOR, 'circle-radius': 7, 'circle-stroke-width': 2,
          'circle-stroke-color': '#fff' } });
      // job pins (amber for SkillBridge, green otherwise)
      map.addLayer({ id: 'jobs-pins-c', source: 'jobs-pins', type: 'circle',
        paint: { 'circle-color': ['case', ['==', ['get', 'sb'], 1], SB_COLOR, JOB_COLOR],
          'circle-radius': ['case', ['==', ['get', 'job_id'], ''], 6, 7],
          'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });

      // Cluster click = an EXPLICIT "show me what's here".
      //  • Co-located stack (colocated flag, individual mode): many jobs at the SAME coordinate —
      //    zooming can't separate them, so show a listing popup so the user sees every opportunity.
      //  • Grid cluster (cluster mode): fit the map to the cluster's TRUE member bounds (from the
      //    backend) so EVERY represented job is contained — never a fixed zoom around the centroid.
      // When the fit movement finishes, moveend fires exactly ONE bounded search. No polling.
      const zoomTo = (e) => {
        const f = e.features[0]; if (!f) return;
        const p = f.properties;
        if (p.colocated === 1 || p.colocated === '1') {
          if (p.kind === 'continuity_cluster') showContColocatedPopup(f);
          else showColocatedPopup(f);
          return;
        }
        autoSearchRef.current = true;
        if (p.w != null && p.s != null && p.e != null && p.n != null) {
          const span = Math.max(Number(p.e) - Number(p.w), Number(p.n) - Number(p.s));
          if (span < 0.0005) {
            // Degenerate/zero-span bounds (all members share one coordinate — e.g. a single
            // facility with many records). fitBounds on a zero-area box can misbehave; center on the
            // point at an individual-mode zoom so the drill-down viewport reliably contains it, then
            // co-located grouping renders one legible count marker instead of invisible stacked pins.
            map.easeTo({ center: f.geometry.coordinates, zoom: Math.max(map.getZoom(), 13), duration: 600 });
          } else {
            map.fitBounds([[Number(p.w), Number(p.s)], [Number(p.e), Number(p.n)]],
              { padding: 60, maxZoom: 15, duration: 600 });
          }
        } else {
          // No bounds (older payload) → fall back to a centroid zoom.
          map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(map.getZoom() + 2.5, 15) });
        }
      };
      // Scrollable popup listing every opportunity stacked at one coordinate. Clicking a row
      // selects that job (card highlight) and closes the popup.
      const showColocatedPopup = (f) => {
        let list = [];
        try { list = JSON.parse(f.properties.jobs || '[]'); } catch { list = []; }
        const count = f.properties.count;
        const loc = f.properties.loc || '';
        const el = document.createElement('div');
        el.style.font = '13px system-ui';
        const rows = list.map(j => {
          const isSb = (j.sb === 1 || j.sb === '1');
          // Normalize SkillBridge presentation to match the list/detail canonical helper so the
          // provider (e.g. "Return 2 Home Port") and a cleaned title are shown consistently —
          // never the raw un-cleaned title/employer that looked confusing on the map.
          const d = isSb ? skillbridgeDisplay({ title: j.title, employer_name: j.employer }) : null;
          const rowTitle = isSb ? d.displayTitle : (j.title || '');
          const rowOrg = isSb ? (d.showProvider ? d.provider : '') : (j.employer || '');
          const score = j.match_score ? `<span style="background:#e7f5ec;color:#0f7d3d;font-weight:600;padding:0 5px;border-radius:8px;font-size:10px;margin-left:4px">Match ${Math.round(Number(j.match_score) * (Number(j.match_score) <= 1 ? 100 : 1))}%</span>` : '';
          const sb = isSb ? `<span style="background:#fef3c7;color:#92400e;font-weight:600;padding:0 5px;border-radius:3px;font-size:9px;text-transform:uppercase;margin-left:4px">SkillBridge</span>` : '';
          const orgHtml = rowOrg ? (isSb ? `<span style="color:#94a3b8">Provider: </span>${esc(rowOrg)}` : esc(rowOrg)) : '';
          return `<div data-jobid="${esc(j.job_id)}" data-score="${esc(j.match_score)}" style="padding:7px 4px;border-bottom:1px solid #f1f5f9;cursor:pointer" onmouseover="this.style.background='#f8fafc'" onmouseout="this.style.background=''"><div style="font-weight:600;color:#0f172a;font-size:12px">${esc(rowTitle)}${sb}${score}</div>${orgHtml ? `<div style="color:#64748b;font-size:11px">${orgHtml}</div>` : ''}</div>`;
        }).join('');
        el.innerHTML = `<div style="font-weight:700;color:#0f172a;margin-bottom:2px">${count} opportunities here</div>${loc ? `<div style="color:#64748b;font-size:12px;margin-bottom:6px">${esc(loc)}</div>` : ''}<div style="max-height:220px;overflow:auto">${rows}</div>`;
        const popup = new maplibregl.Popup({ closeButton: true, maxWidth: '300px' })
          .setLngLat(f.geometry.coordinates).setDOMContent(el).addTo(map);
        el.querySelectorAll('[data-jobid]').forEach(row => {
          row.addEventListener('click', () => {
            const id = row.getAttribute('data-jobid');
            const sc = row.getAttribute('data-score');
            // Open the preview drawer; PRESERVE the stack popup + map context underneath so
            // closing the preview returns to the same popup/viewport (zero map requests).
            if (onOpenJob && id) onOpenJob(id, { match_score: sc || null });
            else if (onSelectJob && id) { onSelectJob(id); popup.remove(); }
          });
        });
      };
      // Popup listing the verified Guard/Reserve facilities/records stacked at one coordinate.
      // Presence only — never implies vacancies/billets. No API call.
      const showContColocatedPopup = (f) => {
        let list = [];
        try { list = JSON.parse(f.properties.facilities || '[]'); } catch { list = []; }
        const count = f.properties.count;
        const loc = f.properties.loc || '';
        const el = document.createElement('div');
        el.style.font = '13px system-ui';
        const rows = list.map(fac => {
          const units = Number(fac.unit_count) > 1
            ? `<span style="color:#475569;font-size:11px;margin-left:4px">${Number(fac.unit_count)} units</span>` : '';
          const link = fac.official_url
            ? `<div><a href="${esc(fac.official_url)}" target="_blank" rel="noopener" style="color:#3b5bdb;font-size:11px">Official source</a></div>` : '';
          return `<div style="padding:6px 4px;border-bottom:1px solid #f1f5f9"><div style="font-weight:600;color:#0f172a;font-size:12px">${esc(fac.site_name || 'Verified location')}${units}</div><div style="color:#64748b;font-size:11px">${esc([fac.city, fac.state].filter(Boolean).join(', '))}${fac.components ? ' · ' + esc(fac.components) : ''}</div>${link}</div>`;
        }).join('');
        const nloc = `${list.length} location${list.length !== 1 ? 's' : ''}`;
        el.innerHTML = `<div style="display:inline-block;background:#eef2ff;color:#3b5bdb;font-weight:600;font-size:10px;padding:1px 6px;border-radius:9px;margin-bottom:4px">Verified military presence</div><div style="font-weight:700;color:#0f172a;margin-bottom:2px">${count} verified presence records</div><div style="color:#64748b;font-size:12px;margin-bottom:4px">${nloc}${loc ? ' · ' + esc(loc) : ''}</div><div style="max-height:220px;overflow:auto">${rows}</div><div style="color:#94a3b8;font-size:10px;margin-top:4px">Presence only — not an available billet.</div>`;
        new maplibregl.Popup({ closeButton: true, maxWidth: '300px' })
          .setLngLat(f.geometry.coordinates).setDOMContent(el).addTo(map);
      };
      map.on('click', 'jobs-clusters-c', zoomTo);
      map.on('click', 'cont-clusters-c', zoomTo);

      map.on('click', 'jobs-pins-c', (e) => {
        const p = e.features[0].properties;
        // Single pin → open the Opportunity Preview directly (no two-step popup). We do NOT
        // recenter/zoom or call the card-highlight flyTo, so the viewport is preserved exactly.
        if (onOpenJob && p.job_id) { onOpenJob(p.job_id, { match_score: p.match_score || null }); return; }
        const scoreHtml = p.match_score ? `<div style="margin-top:4px"><span style="background:#e7f5ec;color:#0f7d3d;font-weight:600;padding:1px 6px;border-radius:9px;font-size:11px">Match ${Math.round(Number(p.match_score) * (Number(p.match_score) <= 1 ? 100 : 1))}%</span></div>` : '';
        const sbTag = p.sb === '1' || p.sb === 1 ? `<span style="background:#fef3c7;color:#92400e;font-weight:600;padding:1px 6px;border-radius:4px;font-size:10px;text-transform:uppercase">SkillBridge</span>` : '';
        new maplibregl.Popup({ closeButton: true, maxWidth: '260px' })
          .setLngLat(e.features[0].geometry.coordinates)
          .setHTML(`<div style="font:13px system-ui"><div style="font-weight:600;color:#0f172a;margin-bottom:2px">${p.title}</div><div style="color:#64748b;font-size:12px">${p.employer || ''}</div><div style="margin-top:4px">${sbTag}</div>${scoreHtml}</div>`)
          .addTo(map);
      });
      map.on('click', 'cont-pins-c', (e) => {
        const p = e.features[0].properties;
        const units = Number(p.unit_count) > 1 ? `<div style="color:#475569;font-size:12px">${p.unit_count} units at this facility</div>` : '';
        const link = p.official_url ? `<a href="${p.official_url}" target="_blank" rel="noopener" style="color:#3b5bdb;font-size:12px">Official source</a>` : '';
        new maplibregl.Popup({ closeButton: true, maxWidth: '280px' })
          .setLngLat(e.features[0].geometry.coordinates)
          .setHTML(`<div style="font:13px system-ui"><div style="display:inline-block;background:#eef2ff;color:#3b5bdb;font-weight:600;font-size:10px;padding:1px 6px;border-radius:9px;margin-bottom:4px">Verified military presence</div><div style="font-weight:600;color:#0f172a">${p.site_name}</div><div style="color:#64748b;font-size:12px">${p.city ? p.city + ', ' : ''}${p.state}</div><div style="color:#475569;font-size:12px;margin-top:2px">${p.components}</div>${units}<div style="margin-top:4px">${link}</div></div>`)
          .addTo(map);
      });
      ['jobs-pins-c', 'cont-pins-c', 'jobs-clusters-c', 'cont-clusters-c'].forEach(id => {
        map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
      });

      setReady(true);
      // Expose the map instance for automated QA of this WebGL layer (pins have no DOM handles).
      // DEV/TEST ONLY — stripped from a Production build via NODE_ENV. Read-only reference.
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') window.__oppMap = map;
      runSearch();                       // one initial bounded request
      // Single moveend handler for ALL map movement. Explicit drill-down/recenter (autoSearchRef)
      // → exactly one bounded search. Ordinary drag/wheel/pinch → only surface "Search this area";
      // the last fetched dataset stays rendered until the user searches (never blanked on zoom).
      map.on('moveend', () => {
        // A programmatic card→pin focus (silentMoveRef) is VISUAL only: no search, no dirty state.
        if (silentMoveRef.current) { silentMoveRef.current = false; return; }
        // Persist viewport for this session so returning from the full job page restores it.
        try {
          const c = map.getCenter();
          sessionStorage.setItem('opps_map_view_v1',
            JSON.stringify({ lng: c.lng, lat: c.lat, zoom: map.getZoom(), ts: Date.now() }));
        } catch { /* ignore */ }
        if (autoSearchRef.current) { autoSearchRef.current = false; runSearch(); }
        else if (isOversized(boundsFromMap(map))) { setTooLarge(true); setDirty(false); }
        else { setTooLarge(false); setDirty(true); }
      });
    });
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep map sized when the container becomes visible / resizes
  useEffect(() => { if (visible && mapRef.current) setTimeout(() => mapRef.current.resize(), 60); },
    [visible]);

  // Observe container size changes and resize the map. Fixes an issue where the map
  // initializes with a 0-height container (e.g., inside a flex column that hasn't
  // laid out yet) and remains stuck at the maplibre default canvas size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (mapRef.current) mapRef.current.resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Layer VISIBILITY toggles — flip MapLibre layer visibility on the already-loaded data. Instant,
  // ZERO API calls, no "Search this area". The sources keep their data so re-enabling is immediate.
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready) return;
    const v = showJobs ? 'visible' : 'none';
    ['jobs-clusters-c', 'jobs-clusters-t', 'jobs-pins-c'].forEach(id => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
    });
  }, [showJobs, ready]);
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready) return;
    const v = showCont ? 'visible' : 'none';
    ['cont-clusters-c', 'cont-clusters-t', 'cont-pins-c'].forEach(id => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
    });
  }, [showCont, ready]);

  // Component filter applies to the CONTINUITY layer only (server-side filter). Changing it issues
  // exactly ONE bounded request for the current viewport; the job layer is unaffected. Skip mount.
  const firstComp = useRef(true);
  useEffect(() => {
    if (firstComp.current) { firstComp.current = false; return; }
    if (!mapRef.current || !ready || !showCont) return;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contComponent]);

  // Jobs / SkillBridge (and remote / job_type) are DATA FILTERS, not visibility toggles: they change
  // WHICH opportunities the bounded query returns. When they change, immediately re-run exactly ONE
  // bounded search for the CURRENT viewport with the CURRENT filter state (read fresh from
  // stateRef in runSearch) — so unchecking AND re-checking SkillBridge both take effect without a
  // page refresh. Skips the first observed value (the map load handler already fired the initial
  // search). Does NOT affect the layer-visibility rule or the manual pan/zoom "Search this area".
  const filterSig = `${opportunityType}|${jobsSelected}|${sbSelected}|${remote}|${jobType}|${savedOnly}|${savedNonce}`;
  const lastFilterSig = useRef(null);
  useEffect(() => {
    if (!ready) return;
    if (lastFilterSig.current === null) { lastFilterSig.current = filterSig; return; }
    if (lastFilterSig.current === filterSig) return;
    lastFilterSig.current = filterSig;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig, ready]);

  // Gently focus a selected job's pin (card → pin). VISUAL only — never triggers a map search
  // or "Search this area". Jobs with no rendered marker (remote / no coords / outside current data)
  // simply don't move the map; the preview still opens (handled by the parent).
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready || !selectedJobId) return;
    const src = map.getSource('jobs-pins'); const data = src && src._data;
    let f = data?.features?.find(ft => ft.properties.job_id === selectedJobId);
    if (!f) {
      // Job may be inside a co-located stack (individual mode) → look it up in the cluster source.
      const cs = map.getSource('jobs-clusters'); const cd = cs && cs._data;
      f = cd?.features?.find(ft => {
        if (!ft.properties.colocated) return false;
        try { return JSON.parse(ft.properties.jobs || '[]').some(j => j.job_id === selectedJobId); }
        catch { return false; }
      });
    }
    if (f) {
      silentMoveRef.current = true;
      map.easeTo({ center: f.geometry.coordinates, zoom: Math.max(map.getZoom(), 11), duration: 600 });
    }
  }, [selectedJobId, ready]);

  // Recenter the map when the user EXPLICITLY changes the Opportunities location/radius controls
  // (focus.key changes), AND when the initial anchor coordinates resolve AFTER the map mounted
  // (default Split/Map entry: the map is created before the resume Home/Base coords load, so init
  // could not center on the anchor — the first focus that arrives WITH coordinates recenters here).
  // Ordinary panning/searching elsewhere is never snapped back. Recenter auto-fires one bounded
  // search via autoSearchRef. If init already consumed this focus, lastFocusKey matches → no move.
  const lastFocusKey = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !focus || focus.key == null) return;
    if (lastFocusKey.current === focus.key) return;
    const prev = lastFocusKey.current;
    lastFocusKey.current = focus.key;
    const hasCoords = focus.lng != null && focus.lat != null;
    // First focus after mount with NO coordinates (Anywhere) → keep the initial viewport as-is
    // (unchanged Anywhere behavior); just record the key so a later coordinate change recenters.
    if (prev === null && !hasCoords) return;
    autoSearchRef.current = true;
    if (hasCoords) {
      map.easeTo({ center: [focus.lng, focus.lat], zoom: focus.zoom ?? map.getZoom() });
    } else {
      // "Anywhere" / no coordinate → national context.
      map.easeTo({ center: US_DEFAULT.center, zoom: US_DEFAULT.zoom });
    }
  }, [focus, ready]);

  // Guard/Reserve info popover: close on Escape (desktop) or a click/tap outside it.
  useEffect(() => {
    if (!infoOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setInfoOpen(false); };
    const onDown = (e) => {
      if (e.target.closest && (e.target.closest('[data-testid="continuity-info-popover"]')
          || e.target.closest('[data-testid="continuity-info-btn"]'))) return;
      setInfoOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [infoOpen]);

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden border border-slate-200" data-testid="opportunities-map">
      <div ref={containerRef} className="w-full h-full" />

      {/* Layer controls — collapsible to recover map space (mobile-first) */}
      <div className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur rounded-lg shadow-md border border-slate-200 text-xs" data-testid="map-layer-controls">
        <button type="button" onClick={toggleLayers} aria-expanded={!layersCollapsed}
          aria-label={layersCollapsed ? 'Expand map layers' : 'Collapse map layers'}
          data-testid="map-layers-toggle"
          className="flex items-center gap-1.5 font-semibold text-slate-700 w-full px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">
          <Layers className="w-3.5 h-3.5" /> Map layers
          <ChevronDown className={`w-3.5 h-3.5 ml-6 transition-transform ${layersCollapsed ? '' : 'rotate-180'}`} />
        </button>
        {!layersCollapsed && (
          <div className="px-2.5 pb-2.5 w-52" data-testid="map-layers-body">
            <label className="flex items-center gap-2 py-0.5 cursor-pointer">
              <input type="checkbox" checked={showJobs} onChange={e => setShowJobs(e.target.checked)} data-testid="map-layer-jobs" />
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: JOB_COLOR }} />
              Opportunities
            </label>
            <div className="flex items-center gap-1.5 py-0.5">
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input type="checkbox" checked={showCont} onChange={e => setShowCont(e.target.checked)} data-testid="map-layer-continuity" />
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: CONT_COLOR }} />
                Verified Guard &amp; Reserve
              </label>
              <button type="button" onClick={() => setInfoOpen(o => !o)} aria-expanded={infoOpen}
                aria-label="About Verified Guard & Reserve coverage" data-testid="continuity-info-btn"
                className="text-slate-400 hover:text-indigo-500 transition-colors flex-shrink-0">
                <Info className="w-3.5 h-3.5" />
              </button>
            </div>
            {showCont && (
              <select value={contComponent} onChange={e => setContComponent(e.target.value)}
                className="mt-1.5 w-full h-7 rounded border border-slate-200 text-[11px] px-1 text-slate-600"
                data-testid="map-continuity-component">
                <option value="">All components</option>
                {CONT_COMPONENTS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        )}
        {infoOpen && !layersCollapsed && (
          <div role="dialog" aria-label="Verified Guard & Reserve coverage" data-testid="continuity-info-popover"
            className="absolute left-0 top-full mt-1 w-64 max-w-[calc(100vw-24px)] bg-white rounded-lg shadow-lg border border-slate-200 p-3 z-20">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
              <span className="text-[11px] leading-snug text-slate-600">Official public-source coverage may not include every unit or facility. Locations indicate verified Guard or Reserve presence, not an available billet.</span>
              <button type="button" onClick={() => setInfoOpen(false)} aria-label="Close" data-testid="continuity-info-close"
                className="ml-auto text-slate-400 hover:text-slate-600 flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search this area — only after the user moves the map to a searchable (bounded) view */}
      {dirty && !tooLarge && !loading && (
        <button onClick={runSearch} data-testid="search-this-area-btn"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-brand text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg hover:bg-brand-dark transition-colors">
          <Search className="w-3.5 h-3.5" /> Search this area
        </button>
      )}

      {/* Oversized viewport — guide the user to zoom in instead of an oversized (rejected) query */}
      {tooLarge && !loading && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-medium px-4 py-2 rounded-full shadow border border-slate-200" data-testid="map-zoom-in-hint">
          <Search className="w-3.5 h-3.5 text-slate-400" /> Zoom in to search this area.
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 bg-white/95 text-slate-600 text-xs font-medium px-4 py-2 rounded-full shadow" data-testid="map-loading">
          <span className="h-3 w-3 rounded-full border-2 border-slate-400 border-t-transparent animate-spin" />
          {initialLoad ? 'Loading opportunities in this area\u2026' : 'Searching this area\u2026'}
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 bg-white text-red-700 text-xs font-medium px-3 py-2 rounded-lg shadow border border-red-200" data-testid="map-error">
          <AlertCircle className="w-3.5 h-3.5" /> Couldn't load this area.
          <button onClick={runSearch} className="inline-flex items-center gap-1 text-brand font-semibold"><RefreshCw className="w-3 h-3" /> Retry</button>
        </div>
      )}
    </div>
  );
}
